import { Link } from "react-router-dom";
import { StatCard } from "@/components/data-display/stat-card";
import { TableState } from "@/components/data-display/table-state";
import { UnavailableValue } from "@/components/data-display/unavailable-value";
import { InfoBanner } from "@/components/ui/banner";
import { Table, TableWrapper } from "@/components/ui/table";
import { WipHint } from "@/components/ui/wip-hint";
import { MetricCard, Panel, SectionHeading } from "@/features/treasury/treasury-wip";

/**
 * `/treasury` — Treasury overview.
 *
 * Layout ported from `.design-ref/prototype/app/(enterprise-wallet)/treasury/page.tsx`
 * (the `TreasuryOverview` export in `features/treasury/treasury.tsx`): header,
 * the five-tile metric row, the risk-flag banner, the Open Opportunities table,
 * and the Top Staking Positions / Venue Balances panel pair.
 *
 * No treasury backend exists in this environment (context/page_pipeline/BOARD.md
 * wave 4, context/page_pipeline/BACKEND_GAPS.md): AUM, idle balances, staking
 * and lending totals, venue exposure, opportunity rows and risk flags were all
 * fabricated before this page was deferred (page-delivery-pipeline.md G6,
 * 2026-09-23). This is a **layout-only** port: the prototype's `TRS_HOME_METRICS`
 * and `TRS_OPPORTUNITIES` sample data are not copied. Every figure renders the
 * unavailable marker, every table renders a designed empty state, and the
 * designer's deferred-to-a-later-frame buttons become real links to the routes
 * that do exist. See report.md → BACKEND GAPS.
 */

/** The copy `UnavailableValue` renders; repeated for string-typed props (StatCard.hint). */
const NOT_AVAILABLE = "Not available yet";

/** Table header cells, matching the designer's `th` classes exactly. */
const TH = "px-4 py-2.5";

export default function TreasuryPage() {
  return (
    <div>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-[24px] font-bold leading-tight tracking-tight text-ink">Treasury</h1>
            <WipHint />
          </div>
          <p className="mt-1 text-sm text-muted">
            Overview of AUM, idle balances, active positions, venue exposure, and risk flags.
          </p>
        </div>
      </div>

      <div role="status" className="mb-5">
        <p className="text-sm text-muted">Treasury is not available in this environment yet</p>
        <p className="text-xs text-muted">
          Staking, lending, and liquidity operations have no backend in this environment yet. This page will show real
          positions once the treasury domain is built.
        </p>
      </div>

      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        <StatCard value="" label="Total AUM" hint={NOT_AVAILABLE} />
        <StatCard value="" label="Idle Balances" hint={NOT_AVAILABLE} />
        <MetricCard
          value="—"
          label="Active Staking"
          sub={
            <>
              <UnavailableValue /> ·{" "}
              <Link to="/treasury/staking" className="font-semibold text-primary hover:underline">
                View staking →
              </Link>
            </>
          }
        />
        <MetricCard
          value="—"
          label="Active Lending"
          sub={
            <>
              <UnavailableValue /> ·{" "}
              <Link to="/treasury/lending" className="font-semibold text-primary hover:underline">
                View lending →
              </Link>
            </>
          }
        />
        <MetricCard
          value="—"
          label="Venue Exposure"
          sub={
            <>
              <UnavailableValue /> ·{" "}
              <Link to="/treasury/liquidity" className="font-semibold text-primary hover:underline">
                View liquidity →
              </Link>
            </>
          }
        />
      </div>

      {/*
        The designer's warning banner carried a risk-flag subject and told the
        reader to review the collateral position. With no risk service there is
        no subject to show, and stating one would be a fabricated alert, so the
        banner keeps its place in the layout and its label, and drops the claim.
      */}
      <InfoBanner variant="info">
        <strong>Risk Flag:</strong> <UnavailableValue /> — risk indicators have no backend in this environment yet.
      </InfoBanner>

      <SectionHeading>Open Opportunities</SectionHeading>
      <TableWrapper className="mb-5">
        <Table className="dense">
          <thead>
            <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
              <th className={TH}>Opportunity</th>
              <th className={TH}>Type</th>
              <th className={TH}>Est. Yield</th>
              <th className={TH}>Risk</th>
              <th className={`${TH} text-right`}>Action</th>
            </tr>
          </thead>
          <tbody>
            <TableState
              variant="empty"
              colSpan={5}
              title="No opportunities"
              description="Opportunity recommendations come from the treasury engine, which has no backend in this environment yet."
            />
          </tbody>
        </Table>
      </TableWrapper>

      <div className="grid gap-5 lg:grid-cols-2">
        <Panel title="Top Staking Positions" bodyClassName="p-0">
          <TableWrapper className="rounded-none border-0">
            <Table className="dense">
              <thead>
                <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
                  <th className={TH}>Asset</th>
                  <th className={TH}>Amount</th>
                  <th className={TH}>APY</th>
                  <th className={TH}>Wallet</th>
                </tr>
              </thead>
              <tbody>
                <TableState
                  variant="empty"
                  colSpan={4}
                  title="No staking positions"
                  description="Staking positions have no backend in this environment yet."
                />
              </tbody>
            </Table>
          </TableWrapper>
        </Panel>

        <Panel title="Venue Balances" bodyClassName="p-0">
          <TableWrapper className="rounded-none border-0">
            <Table className="dense">
              <thead>
                <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
                  <th className={TH}>Venue</th>
                  <th className={TH}>Balance</th>
                  <th className={TH}>% of AUM</th>
                </tr>
              </thead>
              <tbody>
                <TableState
                  variant="empty"
                  colSpan={3}
                  title="No venue balances"
                  description="Venue balances have no backend in this environment yet."
                />
              </tbody>
            </Table>
          </TableWrapper>
        </Panel>
      </div>
    </div>
  );
}
